import { useParams, Link } from 'react-router-dom'
import { useEffect } from 'react'
import { getArticleBySlug, getArticleComponent } from './articles'
import { Button } from '../components/ui/button'
import { Logo } from '../components/Logo'
import { ArrowLeft, Clock, Calendar } from 'lucide-react'

export function ArticlePage() {
  const { slug } = useParams()
  const article = getArticleBySlug(slug)
  const ArticleContent = getArticleComponent(slug)

  useEffect(() => {
    // Scroll to top when article loads
    window.scrollTo(0, 0)

    // Update meta tags for SEO
    if (article) {
      document.title = `${article.title} | PitchMetric`
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription) {
        metaDescription.setAttribute('content', article.description)
      }

      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]')
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta')
        metaKeywords.name = 'keywords'
        document.head.appendChild(metaKeywords)
      }
      metaKeywords.setAttribute('content', article.keywords)

      // Update OG tags
      let ogTitle = document.querySelector('meta[property="og:title"]')
      if (!ogTitle) {
        ogTitle = document.createElement('meta')
        ogTitle.setAttribute('property', 'og:title')
        document.head.appendChild(ogTitle)
      }
      ogTitle.setAttribute('content', article.title)

      let ogDescription = document.querySelector('meta[property="og:description"]')
      if (!ogDescription) {
        ogDescription = document.createElement('meta')
        ogDescription.setAttribute('property', 'og:description')
        document.head.appendChild(ogDescription)
      }
      ogDescription.setAttribute('content', article.description)

      // Track page view
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: article.title,
          page_location: window.location.href,
          page_path: window.location.pathname
        })
      }
    }
  }, [article])

  if (!article) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-400 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/articles">
            <Button className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black">
              View All Articles
            </Button>
          </Link>
        </div>
      </div>
    )
  }

  const handleCTAClick = () => {
    // Track CTA click from article
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'cta_click', {
        event_category: 'engagement',
        event_label: 'article_cta',
        article_title: article.title
      })
    }
    window.location.href = '/?cta=true'
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-black/90 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex h-16 sm:h-20 items-center justify-between">
            <Link to="/">
              <Logo />
            </Link>
            <Button 
              size="sm" 
              className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-4 sm:px-6 text-xs sm:text-sm h-9 sm:h-10"
              onClick={handleCTAClick}
            >
              Get Access
            </Button>
          </div>
        </div>
      </nav>

      {/* Article Content */}
      <article className="pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          {/* Back Button */}
          <Link 
            to="/articles" 
            className="inline-flex items-center text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Articles
          </Link>

          {/* Article Header */}
          <div className="max-w-4xl mx-auto mb-12">
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {article.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-8">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {new Date(article.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {article.readTime}
              </div>
            </div>

            <p className="text-lg sm:text-xl text-gray-300 leading-relaxed">
              {article.description}
            </p>
          </div>

          {/* Article Body */}
          <div className="max-w-4xl mx-auto">
            {ArticleContent && <ArticleContent onCTAClick={handleCTAClick} />}

            {/* Related Articles CTA */}
            <div className="mt-16 pt-8 border-t border-white/10">
              <div className="bg-gradient-to-br from-[#00E5FF]/10 to-purple-500/10 border border-white/10 rounded-2xl p-8 sm:p-12">
                <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-4">
                  Ready to gain your edge?
                </h3>
                <p className="text-gray-300 text-lg mb-6">
                  Get AI-powered sports intelligence for every game. Join early access today.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-black font-medium px-8 h-12"
                    onClick={handleCTAClick}
                  >
                    Get Early Access
                  </Button>
                  <Link to="/articles">
                    <Button 
                      size="lg"
                      variant="outline"
                      className="border-white/10 hover:bg-white/10 text-white font-medium px-8 h-12 w-full sm:w-auto"
                    >
                      Read More Articles
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 sm:py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <Link to="/">
              <Logo className="mb-4 inline-block" />
            </Link>
            <p className="text-sm text-gray-600">
              © 2024 PitchMetric. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

